import { Injectable } from '@angular/core';
import {
    createClient,
    ClientAPI,
    Space,
    Environment as ContentfulEnvironment,
    Asset,
} from "contentful-management";
import { environment } from 'src/environments/environment';
import { Utils } from '../../utils/utils';

@Injectable({
    providedIn: "root",
})
export class ContentfulApiService {
    private client: ClientAPI;
    space: Space;
    contentFullEnv: ContentfulEnvironment;
    private readonly spaceId = environment.contentful.space;
    private readonly accessToken = environment.contentful.accessToken;
    private readonly managementToken = environment.contentful.managementToken;
    private readonly environmentId = environment.contentful.environment;

    private initialized = false;

    constructor() {}

    async initialize(): Promise<void> {
        if (this.initialized) return;

        this.client = createClient({
            space: this.spaceId,
            accessToken: this.accessToken,
        });
        this.space = await this.client.getSpace(environment.contentful.spaceId);
        this.contentFullEnv = await this.space.getEnvironment(
            environment.contentful.environmentId
        );
        if (this.initialized) return;
    }

    async getAllAssets(): Promise<any> {
        if (!this.contentFullEnv) {
            await this.initialize();
        }
        const assets = await this.contentFullEnv.getPublishedAssets();
        const datas =
            assets.items.filter((x) =>
                x.fields.description["en-US"]
                    .toLowerCase()
                    .includes(`Env:${Utils.getEnv()}`.toLowerCase())
            ) ?? [];
        return datas;
    }

    async getAsset(id: string): Promise<Asset> {
        if (!this.contentFullEnv) {
            await this.initialize();
        }
        
        return await this.contentFullEnv.getAsset(id);
    }

    getEntry(id: string) {
        return this.contentFullEnv.getEntry(id).then((response) => response);
    }

    getEntries(content_type: string) {
        return this.contentFullEnv
            .getEntries({ content_type: content_type })
            .then((response) =>
                response.items.filter(
                    (x) =>
                        x.fields?.env &&
                        x.fields?.env["en-US"].toLowerCase() ==
                            Utils.getEnv().toLowerCase()
                )
            );
    }

    getEntryById(id: string) {
        return this.contentFullEnv.getEntry(id).then((entry) => entry);
    }

    async createEntry(contentTypeId: string, fields: any) {
        try {
            fields["env"] = { "en-US": Utils.getEnv() };
            const entry = await this.contentFullEnv.createEntry(contentTypeId, {
                fields,
            });

            return await entry.publish();
        } catch (error) {
            console.error("Erreur de mise à jour:", error);
            throw error;
        }
    }

    async updateEntry(id: string, fields: any) {
        try {
            const entry = await this.contentFullEnv.getEntry(id);

            fields["env"] = { "en-US": Utils.getEnv() };
            entry.fields = fields;

            const updatedEntry = await entry.update();
            await updatedEntry.publish();

            return updatedEntry;
        } catch (error) {
            console.error("Erreur de mise à jour:", error);
            throw error;
        }
    }

    async createAsset(
        file: File,
        fileName = "",
        description = ""
    ): Promise<string> {
        fileName = fileName || file.name;

        let asset = await this.contentFullEnv.createAssetFromFiles({
            fields: {
                title: { "en-US": fileName },
                description: {
                    "en-US": `Env:${Utils.getEnv()} - ${description}`,
                },
                file: {
                    "en-US": {
                        contentType: file.type,
                        fileName: fileName,
                        file: await file.arrayBuffer(),
                    },
                },
            },
        });

        await asset.processForAllLocales();
        asset = await this.contentFullEnv.getAsset(asset.sys.id);
        await asset.publish();

        return asset.sys.id;
    }
}
